import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { HotTable } from "@handsontable/react";
import { registerAllModules } from "handsontable/registry";
import { registerLanguageDictionary, ptBR } from "handsontable/i18n";
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  ListItemIcon,
  Menu,
  MenuItem,
  Paper,
  Popover,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import LoadingPage from "../../loadingPage";
import "handsontable/styles/ht-theme-main.css";
import "handsontable/dist/handsontable.full.min.css";
import {
  Download,
  FilterAltOutlined,
  LocalPrintshopOutlined,
  PictureAsPdf,
  RssFeedOutlined,
  Sensors,
} from "@mui/icons-material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import LogoMW from "../../../assets/logo3.png";
import * as React from "react";
import { customStyles } from "./tableStyle";

registerLanguageDictionary(ptBR);
registerAllModules();

// --- Ícones SVG ---
const EDIT_ICON_SVG = `<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>`;
const DELETE_ICON_SVG = `<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M16 9v10H8V9h8m-1.5-6h-5l-1 1H5v2h14V4h-3.5l-1-1zM18 7H6v12c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7z"/></svg>`;
const INFO_ICON_SVG = `<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M11 17h2v-6h-2v6zm0-8h2V7h-2v2zm1-7C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/></svg>`;

const NetworkTable = React.memo(
  ({
    columns,
    rows,
    isEditable = false,
    setIsEditable,
    equipmentMap = {},
    onEditRow,
    onDeleteRow,
    onEditDescriptionRow,
    loadingTable,
    loading,
    hosts,
    isAdmin,
  }) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === "dark";
    const hotRef = useRef(null);
    const easyMapsOnly =
      window?.config?.REACT_APP_EASYMAPS_ONLY === "true" || false;

    const [printMenuAnchor, setPrintMenuAnchor] = useState(null);
    const [customColumnFilters, setCustomColumnFilters] = useState({});
    const [filterPopover, setFilterPopover] = useState(null);

    useEffect(() => {
      if (isDarkMode) {
        document.body?.classList.add("dark-theme");
        document.body.style.backgroundColor = theme.palette.background.default;
      } else {
        document.body?.classList.remove("dark-theme");
        document.body.style.backgroundColor = theme.palette.background.default;
      }
      document.body.style.backgroundColor = theme.palette.background.default;
    }, [isDarkMode, theme]);

    const actionsRenderer = useCallback(
      (instance, td, row, col, prop, value, cellProperties) => {
        while (td.firstChild) {
          td.removeChild(td.firstChild);
        }

        const container = document.createElement("div");
        container.className = "action-cell-container";

        const editBtn = document.createElement("div");
        editBtn.className = "action-btn edit";
        editBtn.innerHTML = EDIT_ICON_SVG;
        editBtn.dataset.action = "edit";
        editBtn.title = "Editar";

        const deleteBtn = document.createElement("div");
        deleteBtn.className = "action-btn delete";
        deleteBtn.innerHTML = DELETE_ICON_SVG;
        deleteBtn.dataset.action = "delete";
        deleteBtn.title = "Excluir";

        const infoBtn = document.createElement("div");
        infoBtn.className = "action-btn info";
        infoBtn.innerHTML = INFO_ICON_SVG;
        infoBtn.dataset.action = "info";
        infoBtn.title = "Descrição";

        container.appendChild(infoBtn);
        container.appendChild(editBtn);
        container.appendChild(deleteBtn);
        td.appendChild(container);
        td.style.textAlign = "center";
        td.style.verticalAlign = "middle";
      },
      [],
    );

    const chipRenderer = useCallback(
      (instance, td, row, col, prop, value, cellProperties) => {
        while (td.firstChild) {
          td.removeChild(td.firstChild);
        }

        let label = "UNK";
        if (prop === "checks.ping") label = "PING";
        if (prop === "checks.snmp") label = "SNMP";

        const span = document.createElement("span");
        let chipClass = "ht-chip-null";

        if (value === "Ativo") {
          chipClass = "ht-chip-success";
        } else if (value === "Inativo") {
          chipClass = "ht-chip-error";
        }

        span.className = `ht-chip ${chipClass}`;
        span.innerText = label;
        span.dataset.action = "toggle-check";
        span.dataset.prop = prop;

        td.appendChild(span);
        td.className = "chip-cell";
        td.style.textAlign = "center";
        td.style.verticalAlign = "middle";
      },
      [],
    );

    const handleBeforeOnCellMouseDown = useCallback(
      (event, coords, TD) => {
        const hotInstance = hotRef.current.hotInstance;
        const target = event.target;

        if (!target) return;
        if (coords.row < 0) {
          if (coords.col === 0) {
            const currentData = hotInstance.getSourceData();
            const newState = !currentData.every((row) => row.selected);
            hotInstance.batch(() => {
              currentData.forEach((row, index) =>
                hotInstance.setDataAtRowProp(index, "selected", newState),
              );
            });
            event.preventDefault();
            return;
          }
          return;
        }

        if (coords.row >= 0) {
          const prop = hotInstance.colToProp(coords.col);

          if (prop === "checks.ping" || prop === "checks.snmp") {
            if (!isEditable) return;

            if (
              target.dataset.action === "toggle-check" ||
              target.classList?.contains("chip-cell")
            ) {
              const currentVal = hotInstance.getDataAtRowProp(coords.row, prop);
              hotInstance.setDataAtRowProp(coords.row, prop, !currentVal);

              event.preventDefault();
              return;
            }
          }

          if (prop === "actions") {
            const actionBtn = target.closest(".action-btn");
            if (actionBtn) {
              const action = actionBtn.dataset.action;
              const rowData = hotInstance.getSourceDataAtRow(coords.row);

              if (action === "edit") onEditRow?.(rowData);
              else if (action === "delete") onDeleteRow?.(rowData);
              else if (action === "info") onEditDescriptionRow?.(rowData);

              event.preventDefault();
              return;
            }
          }
        }
      },
      [isEditable, onEditRow, onDeleteRow, onEditDescriptionRow],
    );

    const getColHeaders = useCallback((col) => {
      const headers = [
        "Host",
        "IP",
        "Ping",
        "SNMP",
        "Comunidade",
        "Porta ",
        "Fabricante",
        "Modelo",
        "User SSH",
        "Senha SSH",
        "Porta SSH",
        "Proxy",
        "Ações",
      ];

      return headers[col];
    }, []);

    const cellsConfig = useCallback(
      (row, col, prop) => {
        const cellProperties = {};
        const hotInstance = hotRef.current?.hotInstance;

        if (prop === "actions") {
          cellProperties.readOnly = true;
          cellProperties.renderer = actionsRenderer;
          return cellProperties;
        }

        if (prop === "checks.ping" || prop === "checks.snmp") {
          cellProperties.readOnly = true;
          cellProperties.renderer = chipRenderer;
          return cellProperties;
        }

        if (prop === "error_msg") {
          cellProperties.readOnly = true;
          cellProperties.className = "error-cell";
          return cellProperties;
        }

        cellProperties.readOnly = !isEditable;

        if (hotInstance && prop === "modelo") {
          const fabricante = hotInstance.getDataAtRowProp(row, "fabricante");
          cellProperties.source = equipmentMap[fabricante] || [];
        }
        return cellProperties;
      },
      [isEditable, equipmentMap],
    );

    const handleDownloadCSV = () => {
      const headers = [
        "Nome",
        "IP",
        "Fabricante",
        "Modelo",
        "Tipo interface",
        "Porta Interface",
        "Comunidade SNMP",
        "Tipo Acesso",
        "User Acesso",
        "Senha Acesso",
        "Porta Acesso",
        "Proxy",
      ];
      const snmpExample = [
        "Host exemplo 01",
        "168.0.0.1",
        "HUAWEI",
        "BGP - NE8XXX",
        "SNMPv2",
        "161",
        "Comunidade exemplo",
        "SSH",
        "Usuario Exemplo",
        "Senha exemplo",
        "22",
        "zabbix-proxy",
      ];
      const agentExample = [
        "Host exemplo 02",
        "127.0.0.1",
        "MIKROTIK",
        "SWITCH - PADRAO",
        "PING/AGENTE",
        "10050",
        "",
        "",
        "",
        "",
        "",
        "zabbix-proxy",
      ];

      const csvContent = [
        headers.join(","),
        snmpExample.join(","),
        agentExample.join(","),
      ].join("\n");

      const blob = new Blob(["\ufeff", csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        easyMapsOnly ? "Template Host.csv" : "MW Soluções - Template Host.csv",
      );
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    const handleExportDataPDF = () => {
      const hotInstance = hotRef.current?.hotInstance;
      if (!hotInstance) return;

      const exportCols = [
        { prop: "nome", label: "Host" },
        { prop: "ip", label: "IP" },
        { prop: "comunidade", label: "Comunidade" },
        { prop: "portaSnmp", label: "Porta Interface" },
        { prop: "fabricante", label: "Fabricante" },
        { prop: "modelo", label: "Modelo" },
        { prop: "userSsh", label: "User SSH" },
        isAdmin && { prop: "senhaSsh", label: "Senha SSH" },
        { prop: "portaSsh", label: "Porta SSH" },
        { prop: "proxy", label: "Proxy" },
      ].filter(Boolean);

      const getNestedValue = (obj, path) =>
        path.split(".").reduce((acc, key) => acc?.[key], obj);

      const formatValue = (value, prop) => {
        return value ?? "";
      };

      const sourceData = hotInstance.getSourceData();
      const pdfHeaders = exportCols.map((col) => col.label);
      const pdfRows = sourceData.map((row) =>
        exportCols.map((col) =>
          formatValue(getNestedValue(row, col.prop), col.prop),
        ),
      );

      const hexToRgb = (hex) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result
          ? [
              parseInt(result[1], 16),
              parseInt(result[2], 16),
              parseInt(result[3], 16),
            ]
          : [88, 64, 255];
      };

      const doc = new jsPDF({ orientation: "landscape" });

      autoTable(doc, {
        head: [pdfHeaders],
        body: pdfRows,
        styles: { fontSize: 8, cellPadding: 2 },
        theme: "grid",
        headStyles: {
          fillColor: hexToRgb(
            easyMapsOnly ? theme.palette.secondary.main : "#5B2E87",
          ),
        },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        margin: { top: 25, bottom: 25 },
        willDrawPage: (data) => {
          if (!easyMapsOnly) {
            doc.addImage(
              LogoMW,
              "PNG",
              data.settings.margin.left,
              data.settings.margin.top - 14,
              36,
              8,
            );
          }
        },
        didDrawPage: (data) => {
          const pageHeight = doc.internal.pageSize.height;
          const marginRight = doc.internal.pageSize.width - 14;
          const marginLeft = data.settings.margin.left;

          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.1);
          doc.line(marginLeft, pageHeight - 15, marginRight, pageHeight - 15);
          doc.setFontSize(8);
          doc.text(`${data.pageNumber}`, marginRight - 3, pageHeight - 10);
          doc.text(
            easyMapsOnly
              ? `Emitido em ${new Date().toLocaleString("pt-BR")}`
              : `MW Soluções LTDA. Emitido em ${new Date().toLocaleString("pt-BR")}`,
            marginLeft + 1,
            pageHeight - 10,
          );
        },
      });

      doc.save(
        easyMapsOnly
          ? "Hosts cadastrados"
          : `MW Soluções - Hosts cadastrados.pdf`,
      );
    };

    const handleExportDataCSV = () => {
      const hotInstance = hotRef.current?.hotInstance;
      if (!hotInstance) return;

      const exportCols = [
        { prop: "nome", label: "Host" },
        { prop: "ip", label: "IP" },
        { prop: "comunidade", label: "Comunidade" },
        { prop: "portaSnmp", label: "Porta interface" },
        { prop: "fabricante", label: "Fabricante" },
        { prop: "modelo", label: "Modelo" },
        { prop: "userSsh", label: "User SSH" },
        isAdmin && { prop: "senhaSsh", label: "Senha SSH" },
        { prop: "portaSsh", label: "Porta SSH" },
        { prop: "proxy", label: "Proxy" },
      ].filter(Boolean);

      const getNestedValue = (obj, path) =>
        path.split(".").reduce((acc, key) => acc?.[key], obj);

      const sourceData = hotInstance.getSourceData();
      const csvHeaders = exportCols.map((col) => col.label).join(",");
      const csvRows = sourceData.map((row) =>
        exportCols
          .map((col) => {
            const val = getNestedValue(row, col.prop) ?? "";
            const escaped = String(val).replace(/"/g, '""');
            return `"${escaped}"`;
          })
          .join(","),
      );

      const csvContent = [csvHeaders, ...csvRows].join("\n");
      const blob = new Blob(["\ufeff", csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        easyMapsOnly
          ? "Hosts cadastrados.csv"
          : "MW Soluções - Hosts cadastrados.csv",
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };
    const handleAfterChange = useCallback((changes, src) => {
      if (src !== "loadData" && changes?.some(([r, p]) => p === "selected")) {
      }
    }, []);

    const CUSTOM_FILTERS = useMemo(
      () => ({
        2: {
          prop: "checks.ping",
          title: "Filtrar Ping",
          options: [
            { value: "Ativo", label: "Ativo" },
            { value: "Inativo", label: "Inativo" },
            { value: "", label: "Sem dados" },
          ],
        },
        3: {
          prop: "checks.snmp",
          title: "Filtrar SNMP",
          options: [
            { value: "Ativo", label: "Ativo" },
            { value: "Inativo", label: "Inativo" },
            { value: "", label: "Sem dados" },
          ],
        },
      }),
      [],
    );

    const getNestedValue = useCallback((obj, path) => {
      return String(path)
        .split(".")
        .reduce((acc, key) => (acc == null ? acc : acc[key]), obj);
    }, []);

    const normalizeFilterValue = useCallback((value) => {
      return value == null ? "" : value;
    }, []);

    const filteredRows = useMemo(() => {
      const activeFilters = Object.entries(customColumnFilters);
      if (!activeFilters.length) return rows;

      return rows.filter((row) => {
        return activeFilters.every(([col, selectedValues]) => {
          const cfg = CUSTOM_FILTERS[Number(col)];
          if (!cfg) return true;

          const rowValue = normalizeFilterValue(getNestedValue(row, cfg.prop));
          return selectedValues.includes(rowValue);
        });
      });
    }, [
      CUSTOM_FILTERS,
      customColumnFilters,
      getNestedValue,
      normalizeFilterValue,
      rows,
    ]);

    const openCustomFilter = useCallback(
      (anchorEl, col) => {
        const cfg = CUSTOM_FILTERS[col];
        if (!cfg) return;

        const selected = new Set(
          customColumnFilters[col] ?? cfg.options.map((o) => o.value),
        );

        const rect = anchorEl.getBoundingClientRect();
        const anchorPosition = {
          top: rect.bottom,
          left: rect.left,
        };

        setFilterPopover({ anchorPosition, col, selected });
      },
      [CUSTOM_FILTERS, customColumnFilters],
    );

    const closeCustomFilter = useCallback(() => setFilterPopover(null), []);

    const toggleFilterValue = useCallback((value) => {
      setFilterPopover((prev) => {
        if (!prev) return prev;
        const next = new Set(prev.selected);
        if (next.has(value)) next.delete(value);
        else next.add(value);
        return { ...prev, selected: next };
      });
    }, []);

    const applyCustomFilter = useCallback(() => {
      if (!filterPopover) return;
      const { col, selected } = filterPopover;
      const cfg = CUSTOM_FILTERS[col];
      if (!cfg) return;

      const allValues = cfg.options.map((o) => o.value);
      const isAll = allValues.every((v) => selected.has(v));
      const isNone = selected.size === 0;

      setCustomColumnFilters((prev) => {
        const next = { ...prev };
        if (isAll) {
          delete next[col];
        } else if (isNone) {
          next[col] = [];
        } else {
          next[col] = Array.from(selected);
        }
        return next;
      });

      closeCustomFilter();
    }, [CUSTOM_FILTERS, filterPopover, closeCustomFilter]);

    const clearCustomFilter = useCallback(() => {
      if (!filterPopover) return;
      const { col } = filterPopover;
      setCustomColumnFilters((prev) => {
        const next = { ...prev };
        delete next[col];
        return next;
      });
      closeCustomFilter();
    }, [filterPopover, closeCustomFilter]);

    const handleAfterGetColHeader = useCallback(
      (col, TH) => {
        const nativeBtn = TH.querySelector(".changeType");
        if (nativeBtn) nativeBtn.parentElement.removeChild(nativeBtn);

        if (!CUSTOM_FILTERS[col]) return;

        const existing = TH.querySelector(".custom-filter-btn");
        if (existing) existing.parentElement.removeChild(existing);

        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = "custom-filter-btn";
        btn.dataset.col = String(col);
        btn.title = "Filtrar coluna";
        const isActive = Object.prototype.hasOwnProperty.call(
          customColumnFilters,
          col,
        );
        btn.innerHTML =
          '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor"><path d="M4.25 5.61C6.27 8.2 10 13 10 13v6c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-6s3.72-4.8 5.74-7.39A1 1 0 0 0 18.95 4H5.04c-.83 0-1.3.95-.79 1.61z" fill="none" stroke="currentColor" stroke-width="2"/></svg>';
        btn.style.cssText = `
          background: ${isActive ? alpha(theme.palette.primary.main, 0.2) : "transparent"};
          color: ${isActive ? theme.palette.primary.main : theme.palette.text.secondary};
          border: none;
          padding: 2px 4px;
          margin-left: 4px;
          border-radius: 4px;
          cursor: pointer;
          vertical-align: middle;
          line-height: 0;
        `;

        const target =
          TH.querySelector(".relative") || TH.querySelector("div") || TH;
        target.appendChild(btn);
      },
      [CUSTOM_FILTERS, customColumnFilters, theme],
    );

    const handleHeaderClickCapture = useCallback(
      (event) => {
        const btn = event.target.closest(".custom-filter-btn");
        if (!btn) return;
        event.preventDefault();
        event.stopPropagation();
        const col = Number(btn.dataset.col);
        openCustomFilter(btn, col);
      },
      [openCustomFilter],
    );

    const pingAtivos = hosts.filter((h) => h.checks?.ping === true).length;
    const pingInativos = hosts.filter((h) => h.checks?.ping === false).length;

    const snmpAtivos = hosts.filter((h) => h.checks?.snmp === true).length;
    const snmpInativos = hosts.filter((h) => h.checks?.snmp === false).length;

    const cardsConfig = [
      {
        id: "ping",
        title: "PING",
        icon: (
          <RssFeedOutlined fontSize="medium" sx={{ color: "text.secondary" }} />
        ),
        iconBg: alpha(theme.palette.background.textField, 0.7),
        ativos: pingAtivos,
        inativos: pingInativos,
      },
      {
        id: "snmp",
        title: "SNMP",
        icon: <Sensors fontSize="medium" sx={{ color: "text.secondary" }} />,
        iconBg: alpha(theme.palette.background.textField, 0.7),
        ativos: snmpAtivos,
        inativos: snmpInativos,
      },
    ];
    return (
      <Paper sx={{ pt: 2, borderRadius: "16px" }}>
        <Stack
          sx={{
            display: "flex",
            flexDirection: { sm: "column", md: "row" },
            justifyContent: { sm: "center", md: "space-between" },
            alignItems: { sm: "start", md: "center" },
            px: 1,
            gap: 1,
          }}
        >
          <Stack
            sx={{
              display: "flex",
              flexDirection: { sm: "column", md: "row" },
              justifyContent: { sm: "center", md: "space-between" },
              alignItems: { sm: "start", md: "center" },
              gap: 1,
              py: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontSize: "1rem",
                width: "100%",
              }}
            >
              Hosts Cadastrados
              <Chip
                label={loading ? 0 : hosts?.length || 0}
                size="small"
                sx={{
                  backgroundColor: alpha(theme.palette.success.main, 0.2),
                  color:
                    theme.palette.mode === "dark"
                      ? theme.palette.success.light
                      : theme.palette.success.dark,
                  fontWeight: "bold",
                  "&.MuiChip-root": { borderRadius: 1 },
                  "&.MuiChip-root .MuiChip-label": { marginTop: "2px" },
                }}
              />
            </Box>
            <Stack
              sx={{
                flexDirection: { xs: "column", sm: "row" },
                flexWrap: "nowrap",
                gap: 1.5,
                width: "100%",
              }}
            >
              {cardsConfig.map((card) => (
                <Tooltip
                  key={card.id}
                  title={`${card.title}: ${card.ativos} ativos / ${card.inativos} inativos`}
                  enterTouchDelay={0}
                  arrow
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "flex-start",
                      alignItems: "center",
                      border: `1px solid ${theme.palette.background.textField || theme.palette.divider}`,
                      px: 1,
                      py: 0.5,
                      borderRadius: "8px",
                      gap: 1.5,
                      flex: { xs: "1 1 100%", sm: "1 1 200px", md: "1 1 0" },
                      minWidth: { xs: "none", sm: "200px" },
                      maxWidth: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <Stack
                      sx={{
                        height: "36px",
                        width: "36px",
                        backgroundColor: card.iconBg,
                        borderRadius: "8px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      {card.icon}
                    </Stack>

                    <Stack
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        minWidth: 0,
                        width: "100%",
                        gap: 0.2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          fontWeight: "bold",
                          letterSpacing: "0.5px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          m: 0,
                        }}
                        color="text.secondary"
                      >
                        {card.title}
                      </Typography>

                      <Stack
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 0.8,
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        <Typography
                          noWrap
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: theme.palette.success.main,
                            flexShrink: 1,
                          }}
                        >
                          {card.ativos} ativos
                        </Typography>

                        <Typography
                          sx={{
                            color: theme.palette.text.disabled,
                            fontSize: "14px",
                            flexShrink: 0,
                          }}
                        >
                          ·
                        </Typography>

                        <Typography
                          noWrap
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: theme.palette.error.main,
                            flexShrink: 1,
                          }}
                        >
                          {card.inativos} inativos
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                </Tooltip>
              ))}
            </Stack>
          </Stack>
          <Stack sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
            <Button
              size="medium"
              variant="text"
              startIcon={<Download />}
              onClick={handleDownloadCSV}
            >
              Exportar template
            </Button>

            <Button
              size="medium"
              variant="text"
              startIcon={<LocalPrintshopOutlined />}
              onClick={(e) => setPrintMenuAnchor(e.currentTarget)}
            >
              Imprimir
            </Button>
          </Stack>
        </Stack>

        <Menu
          anchorEl={printMenuAnchor}
          open={Boolean(printMenuAnchor)}
          onClose={() => setPrintMenuAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          sx={{
            "& .MuiPaper-root": {
              backgroundColor: theme.palette.background.textField,
            },
          }}
        >
          <MenuItem
            onClick={() => {
              handleExportDataPDF();
              setPrintMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              <PictureAsPdf fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2">Baixar como PDF</Typography>
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleExportDataCSV();
              setPrintMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              <Download fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2">Baixar como CSV</Typography>
          </MenuItem>
        </Menu>

        <style>{customStyles(theme, isDarkMode)}</style>
        <Divider />
        <Box style={{ margin: "0 auto" }}>
          <Box
            className={`custom-table-wrapper ${!isEditable ? "view-mode" : ""}`}
            onClickCapture={handleHeaderClickCapture}
            sx={{
              padding: "0px",
              minHeight: "450px",
              overflow: "auto",
              position: "relative",
            }}
          >
            {loadingTable ? (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: alpha(theme.palette.background.paper, 0.7),
                  zIndex: 10,
                  borderRadius: "16px",
                }}
              >
                <LoadingPage mt={"5%"} />
              </Box>
            ) : (
              <HotTable
                key={"easyAdd"}
                id="easyAdd"
                ref={hotRef}
                data={filteredRows}
                licenseKey="non-commercial-and-evaluation"
                rowHeaders={false}
                height="55vh"
                stretchH="all"
                rowHeights={10}
                cells={cellsConfig}
                filters={false}
                afterChange={handleAfterChange}
                afterGetColHeader={handleAfterGetColHeader}
                beforeOnCellMouseDown={handleBeforeOnCellMouseDown}
                colHeaders={getColHeaders}
                columns={columns}
                manualColumnResize={true}
                language={ptBR.languageCode}
                loading={loadingTable}
              />
            )}
          </Box>
        </Box>

        <Popover
          open={Boolean(filterPopover)}
          anchorReference="anchorPosition"
          anchorPosition={filterPopover?.anchorPosition}
          onClose={closeCustomFilter}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          slotProps={{
            paper: {
              sx: {
                p: 1.5,
                minWidth: 200,
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.stroke.main}`,
                borderRadius: 2,
              },
            },
          }}
        >
          {filterPopover && (
            <>
              <Stack
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "start",
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, color: theme.palette.text.primary }}
                >
                  {CUSTOM_FILTERS[filterPopover.col]?.title}
                </Typography>
                <FilterAltOutlined
                  fontSize="small"
                  sx={{ color: "text.secondary" }}
                />
              </Stack>
              <Divider sx={{ marginX: "-12px" }} />
              <Stack>
                {CUSTOM_FILTERS[filterPopover.col]?.options.map((opt) => (
                  <FormControlLabel
                    sx={{ mb: "-5px" }}
                    key={opt.value}
                    control={
                      <Checkbox
                        size="small"
                        checked={filterPopover.selected.has(opt.value)}
                        onChange={() => toggleFilterValue(opt.value)}
                      />
                    }
                    label={<Typography variant="body2">{opt.label}</Typography>}
                  />
                ))}
              </Stack>
              <Divider
                sx={{
                  marginX: "-12px",
                }}
              />
              <Stack
                direction="row"
                justifyContent="flex-end"
                spacing={1}
                sx={{
                  mt: 1,
                  pt: 1,
                }}
              >
                <Button size="small" onClick={clearCustomFilter}>
                  Limpar
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={applyCustomFilter}
                >
                  Aplicar
                </Button>
              </Stack>
            </>
          )}
        </Popover>
      </Paper>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.rows === nextProps.rows &&
      prevProps.loadingTable === nextProps.loadingTable &&
      prevProps.isEditable === nextProps.isEditable &&
      prevProps.columns === nextProps.columns
    );
  },
);

export default NetworkTable;
